import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowUpLeft, AlertCircle, Lightbulb, ChevronLeft } from 'lucide-react';
import DemoDashboard from './DemoDashboard';
import { useLanguage, getMainTranslation } from '../hooks/useLanguage';
import { DemoLimitationsModal } from '../components/Dashboard/DemoLimitationsModal';
import { HowItWorksModal } from '../components/Dashboard/HowItWorksModal';
import { SaveCancelButtons } from '../components/Dashboard/SaveCancelButtons';
import { FieldOverviewPanel } from '../components/Dashboard/FieldOverviewPanel';
import { ValidationErrors } from '../components/Dashboard/DeclarationSummary/ValidationErrors';
import { TaricJsonErrors } from '../components/Main/TaricJsonErrors';
import { useSelector } from 'react-redux';
import { hasValidationErrors, hasTaricJsonErrors } from '../utils/errorCheckUtils';
import english from '../assets/united-states.png';
import spanish from '../assets/spain.png';
import '../App.css';
import '../components/CreditSnippet.css';

const Demo = ({ isDarkMode, setIsDarkMode }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSpanish, setIsSpanish] = useLanguage();
  const [isLimitationsModalOpen, setIsLimitationsModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  
  // Get rate limit from Redux state (updated from API responses)
  const rateLimitFromRedux = useSelector((state) => state.ui?.rateLimit);
  const rateLimit = useMemo(() => {
    return rateLimitFromRedux || {
      remaining: 5,
      maxRequests: 5
    };
  }, [rateLimitFromRedux]);
  
  // Track if user has manually closed the panel despite errors (to prevent auto-expand)
  const userManuallyClosedRef = useRef(false);
  
  const [isFieldOverviewCollapsed, setIsFieldOverviewCollapsed] = useState(() => {
    // On refresh, default to collapsed (true) - we'll check for errors and expand if needed
    // This ensures the panel doesn't auto-open just because there's editableData
    return true;
  });
  const [activeSubTab, setActiveSubTab] = useState('allFields');

  // Set isDemo in localStorage immediately (synchronously) when component renders
  // This ensures it's set before any child components mount and their effects run
  if (typeof window !== 'undefined') {
    localStorage.setItem('isDemo', 'true');
  }

  // Get active workflow session ID and state (errors are per-workflow only)
  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
  const activeWorkflowSessionState = useSelector((state) => {
    const sessionId = state.workflowSessions?.activeSessionId;
    return sessionId ? state.workflowSessions?.sessions?.[sessionId]?.state : null;
  });

  // CRITICAL: TARIC JSON errors are per-workflow only (no tabs/global fallback)
  const taricJsonErrors = useMemo(() => {
    const errors = activeWorkflowSessionState?.taricJsonErrors;
    return hasTaricJsonErrors(errors) ? errors : null;
  }, [activeWorkflowSessionState?.taricJsonErrors]);

  // CRITICAL: Validation errors are per-workflow only (no tabs/global fallback)
  const validationErrors = useMemo(() => {
    return activeWorkflowSessionState?.validationErrors || [];
  }, [activeWorkflowSessionState?.validationErrors]);

  // Check if there are errors to determine if panel should auto-expand
  const hasErrors = useMemo(() => {
    return hasValidationErrors(validationErrors) || hasTaricJsonErrors(taricJsonErrors);
  }, [validationErrors, taricJsonErrors]);

  // CRITICAL: Get extractedData from workflow session state first, then fallback to global state
  // Priority: Workflow session state > Global state
  // Normalize to array format for consistency
  const extractedData = useSelector(state => {
    // Priority 1: Active workflow session's extractedData
    const workflowSessionsState = state.workflowSessions;
    const currentActiveSessionId = workflowSessionsState?.activeSessionId;
    if (currentActiveSessionId) {
      const workflowExtractedData = workflowSessionsState?.sessions?.[currentActiveSessionId]?.state?.extractedData;
      if (workflowExtractedData) {
        // Normalize to array format
        return Array.isArray(workflowExtractedData) 
          ? workflowExtractedData 
          : (workflowExtractedData && typeof workflowExtractedData === 'object' ? [workflowExtractedData] : []);
      }
    }
    // Priority 2: Fallback to global state
    const globalData = state.documents.extractedData;
    if (globalData) {
      return Array.isArray(globalData) 
        ? globalData 
        : (globalData && typeof globalData === 'object' ? [globalData] : []);
    }
    return null;
  });

  // CRITICAL: Get editableData from workflow session state first, then tabs, then global state
  // Priority: Workflow session state > Tab state > Global state
  const editableData = useSelector(state => {
    // Priority 1: Active workflow session's editableData
    const workflowSessionsState = state.workflowSessions;
    const currentActiveSessionId = workflowSessionsState?.activeSessionId;
    if (currentActiveSessionId) {
      const workflowEditableData = workflowSessionsState?.sessions?.[currentActiveSessionId]?.state?.editableData;
      if (workflowEditableData) {
        return workflowEditableData;
      }
    }
    // Priority 2: Active tab's editableData
    const tabsState = state.tabs;
    if (tabsState && tabsState.activeTabId && tabsState.tabs[tabsState.activeTabId]) {
      return tabsState.tabs[tabsState.activeTabId].editableData;
    }
    // Priority 3: Fallback to global state
    return state.documents.editableData;
  });

  // Memoize extractedData for FieldOverviewPanel
  const fieldOverviewExtractedData = useMemo(() => {
    const dataToUse = editableData || extractedData;
    if (!dataToUse) {
      return null;
    }
    return Array.isArray(dataToUse) 
      ? dataToUse 
      : (dataToUse && typeof dataToUse === 'object' ? [dataToUse] : []);
  }, [editableData, extractedData]);

  // Track if this is the initial mount (to check for errors on refresh)
  const isInitialMountRef = useRef(true);
  const hasCheckedInitialErrorsRef = useRef(false);
  
  // On initial mount or when errors become available, check if we should expand
  useEffect(() => {
    // Wait for errors to be available before making decision
    if (isInitialMountRef.current && !hasCheckedInitialErrorsRef.current) {
      // Check if there are errors on refresh
      if (hasErrors) {
        // Errors exist - auto-expand the panel
        setIsFieldOverviewCollapsed(false);
        localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(false));
      } else {
        // No errors on refresh - keep it collapsed (even if there's editableData)
        setIsFieldOverviewCollapsed(true);
        localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(true));
      }
      hasCheckedInitialErrorsRef.current = true;
      isInitialMountRef.current = false;
    }
  }, [hasErrors]); // Re-check when errors become available
  
  // Auto-expand field overview panel when errors appear after initial check
  // Only auto-expand if user hasn't manually closed it
  useEffect(() => {
    if (hasCheckedInitialErrorsRef.current && hasErrors && !userManuallyClosedRef.current) {
      // Errors appeared - auto-expand the panel
      setIsFieldOverviewCollapsed(false);
      localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(false));
    }
    // Reset manual close flag when errors clear (so it can auto-expand again if errors return)
    if (!hasErrors) {
      userManuallyClosedRef.current = false;
    }
  }, [hasErrors]);

  // Check if registration data exists in sessionStorage
  // If email, company, or expectedDuas are missing, redirect to /register
  useEffect(() => {
    const savedEmail = sessionStorage.getItem('register_email');
    const savedCompany = sessionStorage.getItem('register_company');
    const savedExpectedDuas = sessionStorage.getItem('register_expectedDuas');

    // If any of the required fields are missing, redirect to register page
    if (!savedEmail || !savedCompany || !savedExpectedDuas) {
      console.log('Missing registration data in sessionStorage, redirecting to /register');
      navigate('/register');
    }
  }, [navigate]);

  // Open limitations modal when component mounts
  useEffect(() => {
    setIsLimitationsModalOpen(true);
    
    // Clean up on unmount - remove isDemo flag
    return () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('isDemo');
      }
    };
  }, []);

  const handleReturnToLogin = () => {
    navigate('/login');
  };

  return (
    <div className={`${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Main Content */}
      <div className="w-full flex max-h-screen overflow-hidden">
        <div className="flex-1 overflow-y-auto" id="demo-container">
        {/* Header with Demo Mode and Buttons */}
        <div className="bg-white sticky top-0 left-0 right-0 z-50 container mx-auto px-10 py-6 flex justify-between items-center gap-6">
          {/* Demo Mode Text */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={handleReturnToLogin}>
            <ChevronLeft className='w-5 h-5 text-yellow-800 stroke-yellow-800' />
            <p className="truncate font-mono text-yellow-800 text-sm font-medium">
              {isSpanish ? 'Modo Demo' : 'Demo Mode'}
            </p>
          </div>
          
          {/* Right Side Buttons */}
          <div className="flex gap-2 items-center">
            {/* Save/Cancel Buttons */}
            <SaveCancelButtons />
            
            {/* Rate Limit Display */}
            <div className={`${rateLimit.remaining >= 2 ? 'green-gradient-2' : 'red-gradient-2'} flex items-center justify-center rounded-full shadow-mdd px-4 py-1 h-10 min-h-10 font-medium transition-colors`}>
              <span className="text-xs font-bold w-max">
                <span className='shine'>
                  Rate Limit {rateLimit.remaining}
                </span>
                <span className='text-[10px] opacity-50 font-normal leading-loose ml-1 relative bottom-[2px]'>/ {rateLimit.maxRequests}</span>
              </span>
            </div>
            
            {/* Language Selector */}
            <button
              onClick={() => { setIsSpanish(!isSpanish) }}
              className={`cursor-pointer h-10 flex items-center gap-2 py-2 px-4 rounded-full transition-colors ${isSpanish ? "bg-yellow-100" : "bg-blue-100"}`}
            >
              <img src={isSpanish ? spanish : english} alt="Language" className="h-5" /> 
              <p className="text-sm">{isSpanish ? getMainTranslation('esp', isSpanish) : getMainTranslation('eng', isSpanish)}</p>
            </button>
            
            {/* Demo Limitations Button */}
            <button
              onClick={() => setIsLimitationsModalOpen(true)}
              className="truncate h-10 bg-red-50 hover:bg-red-100 py-2 px-4 rounded-full flex items-center gap-2 transition-colors"
            >
              <AlertCircle className='w-5 h-5 text-red-500 stroke-red-500' />
              <p className="text-sm text-red-500 font-medium">
                {isSpanish ? 'Limitaciones de la Demo' : 'Demo Limitations'}
              </p>
            </button>
            
            {/* How It Works Button */}
            <button
              onClick={() => setIsHowItWorksModalOpen(true)}
              className="truncate h-10 bg-blue-100 hover:bg-blue-200 py-2 px-4 rounded-full flex items-center gap-2 transition-colors"
            >
              <Lightbulb className='w-5 h-5 text-blue-600 stroke-blue-600' />
              <p className="text-sm text-blue-600 font-medium">
                {isSpanish ? 'Cómo Funciona' : 'How It Works'}
              </p>
            </button>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="container mx-auto px-10 pt-8 pb-40">
          <DemoDashboard 
            setBacktoDashboardClicked={() => {}}
            backtoDashboardClicked={false}
            scrollToID=""
            setIsStripeModalOpen={() => {}}
          />
        </div>

        </div>

        {/* Field Overview Panel - Positioned absolutely like in Main.js */}
        {(() => {
          // Only render if we have actual data
          const hasData = fieldOverviewExtractedData && fieldOverviewExtractedData.length > 0;
          
          return hasData;
        })() && (
          <FieldOverviewPanel
            paddingTop="pt-6 "
            extractedData={fieldOverviewExtractedData}
            activeSubTab={activeSubTab}
            setActiveSubTab={setActiveSubTab}
            isSpanish={isSpanish}
            validationErrors={validationErrors}
            taricJsonErrors={taricJsonErrors}
            ValidationErrors={ValidationErrors}
            TaricJsonErrors={TaricJsonErrors}
            location={{ pathname: '/demo' }}
            isFieldOverviewCollapsed={isFieldOverviewCollapsed}
            setIsFieldOverviewCollapsed={(collapsed) => {
              // Track if user manually closed the panel while errors exist
              if (collapsed && hasErrors) {
                // User manually closed it despite errors - respect their choice
                userManuallyClosedRef.current = true;
              } else if (!collapsed) {
                // User manually opened it - allow auto-expand on future errors
                userManuallyClosedRef.current = false;
              }
              setIsFieldOverviewCollapsed(collapsed);
              localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(collapsed));
            }}
          />
        )}
      </div>

      {/* Demo Limitations Modal */}
      <DemoLimitationsModal 
        isOpen={isLimitationsModalOpen}
        onClose={() => setIsLimitationsModalOpen(false)}
      />

      {/* How It Works Modal */}
      <HowItWorksModal 
        isOpen={isHowItWorksModalOpen}
        onClose={() => setIsHowItWorksModalOpen(false)}
      />
    </div>
  );
};

export default Demo;

